import * as THREE from 'three';
import { globalStores } from '@/editor/stores';
import { FurnitureAssets } from './furnitureAssets';
import { ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { pixelRatio2d } from '@/editor/math/const';

// 每种模型的 InstancedMesh 池
interface InstancePool {
  instancedMesh: THREE.InstancedMesh;
  entityToInstanceId: Map<string, number>;
  nextInstanceId: number;
  freeIds: number[];
  svgContent: string; // 缓存对应的 svgContent
}

// 实例注册信息
interface InstanceRegistration {
  modelId: string;
  instanceId: number;
}

const INITIAL_CAPACITY = 64;
const GROWTH_FACTOR = 2;
const Z_OFFSET_PER_LEVEL = 0.0001;
// Pool 收缩阈值：当使用率低于此值时考虑收缩
const SHRINK_THRESHOLD = 0.25;
// Pool 最小容量，低于此值不收缩
const MIN_POOL_CAPACITY = 32;

// 静态矩阵对象，避免每次更新时创建新对象
const _tempMatrix = new THREE.Matrix4();
const _offsetMatrix = new THREE.Matrix4();
const _flipMatrix = new THREE.Matrix4().makeRotationX(Math.PI);
const _scaleMatrix = new THREE.Matrix4();
const _rotationMatrix = new THREE.Matrix4();
const _positionMatrix = new THREE.Matrix4();

export interface IFurnitureTransformParams {
  position: { x: number; y: number };
  rotation: number;
  width: number;
  thickness: number;
}

export interface IFurnitureAssetInfo {
  originWidth: number;
  originHeight: number;
  zOffset: number;
}

/**
 * 家具 InstancedMesh 管理器
 * 使用 modelId 作为 key，负责池管理、matrix 计算
 */
export class FurnitureInstanceManager {
  private _pools = new Map<string, InstancePool>();
  private _entityRegistrations = new Map<string, InstanceRegistration>();
  private _zLevelCounter = 0;
  private _entityZLevel = new Map<string, number>();
  private _dirtyPools = new Set<InstancePool>();
  private _updateScheduled = false;
  private _displayOrder: number;

  constructor(diaplayOrder: number) {
    this._displayOrder = diaplayOrder;
  }

  private scene = globalStores.editorStateStore.sceneInfo.scene2d.scene;

  /**
   * 获取实体的 z 层级偏移量
   */
  getZOffset(entityId: string): number {
    const level = this._entityZLevel.get(entityId) ?? 0;
    return level * Z_OFFSET_PER_LEVEL;
  }

  /**
   * 获取 asset 信息（用于 Model2d 渲染 hitbox 和 workPoints）
   */
  getAssetInfo(entityId: string): IFurnitureAssetInfo | null {
    const registration = this._entityRegistrations.get(entityId);
    if (!registration) return null;

    const pool = this._pools.get(registration.modelId);
    if (!pool) return null;

    const asset = FurnitureAssets.getAsset(pool.svgContent);
    return {
      originWidth: asset.originWidth,
      originHeight: asset.originHeight,
      zOffset: this.getZOffset(entityId)
    };
  }

  /**
   * 取出实体当前的实例矩阵。
   * setMatrixAt 是同步写入的（markDirty 只延后 GPU 上传），所以 CPU 侧数据始终是最新的。
   * 离屏重建场景（如缩略图截图）靠它复刻位置/缩放/翻转/叠放顺序，避免另写一份变换逻辑。
   */
  getInstanceMatrix(entityId: string, target = new THREE.Matrix4()): THREE.Matrix4 | null {
    const registration = this._entityRegistrations.get(entityId);
    if (!registration) return null;

    const pool = this._pools.get(registration.modelId);
    if (!pool) return null;

    pool.instancedMesh.getMatrixAt(registration.instanceId, target);
    return target;
  }

  /**
   * 注册一个家具实例
   * @param entityId 实体 ID
   * @param modelId 模型 ID（库模型的 key）
   * @param svgContent SVG 内容
   * @returns 是否注册成功
   */
  registerInstance(entityId: string, modelId: string, svgContent: string): boolean {
    // 如果已注册，先注销
    if (this._entityRegistrations.has(entityId)) {
      this.unregisterInstance(entityId);
    }

    // 获取或创建池
    let pool: InstancePool | undefined = this._pools.get(modelId);
    if (!pool) {
      const newPool = this.createPool(modelId, svgContent);
      if (!newPool) return false;
      pool = newPool;
      this._pools.set(modelId, pool);
    }

    // 分配实例 ID
    let instanceId: number;
    const freeId = pool.freeIds.pop();
    if (freeId !== undefined) {
      instanceId = freeId;
    } else {
      if (pool.nextInstanceId >= pool.instancedMesh.count) {
        this.growPool(pool);
      }
      instanceId = pool.nextInstanceId++;
    }

    pool.entityToInstanceId.set(entityId, instanceId);

    // 保存注册信息
    this._entityRegistrations.set(entityId, { modelId, instanceId });

    // 分配 z 层级（始终递增，保证后放置的家具不会被前面的遮挡）
    if (!this._entityZLevel.has(entityId)) {
      this._entityZLevel.set(entityId, this._zLevelCounter++);
    }

    // 初始化为隐藏状态
    const hideMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
    pool.instancedMesh.setMatrixAt(instanceId, hideMatrix);
    this.markDirty(pool);

    return true;
  }

  /**
   * 更新实体的变换
   */
  updateTransform(entityId: string, params: IFurnitureTransformParams): void {
    const registration = this._entityRegistrations.get(entityId);
    if (!registration) return;

    const pool = this._pools.get(registration.modelId);
    if (!pool) return;

    const asset = FurnitureAssets.getAsset(pool.svgContent);
    const matrix = this.computeMatrix(entityId, params, asset);

    pool.instancedMesh.setMatrixAt(registration.instanceId, matrix);
    this.markDirty(pool);
  }

  /**
   * 隐藏实体（设置为缩放0）
   */
  hideInstance(entityId: string): void {
    const registration = this._entityRegistrations.get(entityId);
    if (!registration) return;

    const pool = this._pools.get(registration.modelId);
    if (!pool) return;

    const hideMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
    pool.instancedMesh.setMatrixAt(registration.instanceId, hideMatrix);
    this.markDirty(pool);
  }

  /**
   * 注销家具实例
   */
  unregisterInstance(entityId: string): void {
    const registration = this._entityRegistrations.get(entityId);
    if (!registration) return;

    const pool = this._pools.get(registration.modelId);
    if (pool) {
      const hideMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
      pool.instancedMesh.setMatrixAt(registration.instanceId, hideMatrix);
      this.markDirty(pool);

      pool.entityToInstanceId.delete(entityId);
      pool.freeIds.push(registration.instanceId);

      // 如果该 pool 没有活跃实例了，销毁它以释放内存和减少 drawCall
      if (pool.entityToInstanceId.size === 0) {
        this.disposePool(registration.modelId);
      } else {
        // 检查是否需要收缩 pool
        this.checkAndShrinkPool(pool);
      }
    }

    // 清理 z-level（不回收，避免后放置的家具被遮挡）
    this._entityZLevel.delete(entityId);

    this._entityRegistrations.delete(entityId);
  }

  /**
   * 计算变换矩阵
   */
  private computeMatrix(
    entityId: string,
    params: IFurnitureTransformParams,
    asset: { originWidth: number; originHeight: number; offsetX: number; offsetY: number }
  ): THREE.Matrix4 {
    const { position, rotation, width, thickness } = params;

    // 计算缩放
    const scaleX = width / asset.originWidth / pixelRatio2d;
    const scaleY = thickness / asset.originHeight / pixelRatio2d;

    // 1. 偏移（在翻转后的局部坐标系中）
    _offsetMatrix.makeTranslation(asset.offsetX, -asset.offsetY, 0);

    // 2. 翻转 - 使用预计算的静态矩阵

    // 3. 缩放
    _scaleMatrix.makeScale(scaleX, scaleY, 1);

    // 4. 旋转 (Z 轴)
    _rotationMatrix.makeRotationZ(rotation);

    // 5. 平移到世界位置（包含 z 偏移用于遮挡排序）
    const zOffset = this.getZOffset(entityId);
    _positionMatrix.makeTranslation(position.x / pixelRatio2d, position.y / pixelRatio2d, zOffset);

    // 组合矩阵: position * rotation * scale * flip * offset
    _tempMatrix
      .copy(_positionMatrix)
      .multiply(_rotationMatrix)
      .multiply(_scaleMatrix)
      .multiply(_flipMatrix)
      .multiply(_offsetMatrix);

    // 直接返回静态矩阵，setMatrixAt 会自动复制数据
    return _tempMatrix;
  }

  /**
   * 标记 pool 为脏，安排批量更新
   */
  private markDirty(pool: InstancePool): void {
    this._dirtyPools.add(pool);
    if (!this._updateScheduled) {
      this._updateScheduled = true;
      queueMicrotask(() => this.flushUpdates());
    }
  }

  /**
   * 刷新所有脏 pool
   */
  private flushUpdates(): void {
    this._updateScheduled = false;
    for (const pool of this._dirtyPools) {
      pool.instancedMesh.instanceMatrix.needsUpdate = true;
    }
    this._dirtyPools.clear();
  }

  private createPool(modelId: string, svgContent: string): InstancePool | null {
    const asset = FurnitureAssets.getAsset(svgContent);

    if (!asset.mergedGeometry) {
      return null;
    }

    const instancedMesh = new THREE.InstancedMesh(
      asset.mergedGeometry,
      FurnitureAssets.instancedMaterials,
      INITIAL_CAPACITY
    );

    instancedMesh.frustumCulled = false;
    instancedMesh.renderOrder = ENTITY_RENDER_ORDER.furniture;

    const hideMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let i = 0; i < INITIAL_CAPACITY; i++) {
      instancedMesh.setMatrixAt(i, hideMatrix);
    }
    instancedMesh.instanceMatrix.needsUpdate = true;

    instancedMesh.position.setZ(this._displayOrder);
    this.scene.add(instancedMesh);

    return {
      instancedMesh,
      entityToInstanceId: new Map(),
      nextInstanceId: 0,
      freeIds: [],
      svgContent
    };
  }

  private growPool(pool: InstancePool): void {
    const oldMesh = pool.instancedMesh;
    const newCapacity = oldMesh.count * GROWTH_FACTOR;

    const asset = FurnitureAssets.getAsset(pool.svgContent);
    if (!asset.mergedGeometry) return;

    const newMesh = new THREE.InstancedMesh(asset.mergedGeometry, FurnitureAssets.instancedMaterials, newCapacity);

    newMesh.frustumCulled = false;
    newMesh.renderOrder = ENTITY_RENDER_ORDER.furniture;

    const oldArray = oldMesh.instanceMatrix.array;
    const newArray = newMesh.instanceMatrix.array;
    newArray.set(oldArray);

    const hideMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let i = oldMesh.count; i < newCapacity; i++) {
      newMesh.setMatrixAt(i, hideMatrix);
    }
    newMesh.instanceMatrix.needsUpdate = true;

    this.scene.remove(oldMesh);
    newMesh.position.setZ(this._displayOrder);
    this.scene.add(newMesh);

    oldMesh.dispose();
    pool.instancedMesh = newMesh;
  }

  /**
   * 检查并收缩 pool（如果使用率过低）
   */
  private checkAndShrinkPool(pool: InstancePool): void {
    const capacity = pool.instancedMesh.count;
    const activeCount = pool.entityToInstanceId.size;
    const utilization = activeCount / capacity;

    // 如果容量大于最小值且使用率低于阈值，考虑收缩
    if (capacity > MIN_POOL_CAPACITY && utilization < SHRINK_THRESHOLD) {
      this.shrinkPool(pool);
    }
  }

  /**
   * 收缩 pool 到合适的大小
   */
  private shrinkPool(pool: InstancePool): void {
    const activeCount = pool.entityToInstanceId.size;
    // 新容量：活跃数量的 2 倍，但不小于最小容量
    const newCapacity = Math.max(MIN_POOL_CAPACITY, Math.ceil(activeCount * 2));
    const oldCapacity = pool.instancedMesh.count;

    // 如果新容量和旧容量相差不大，不收缩
    if (newCapacity >= oldCapacity * 0.75) return;

    const asset = FurnitureAssets.getAsset(pool.svgContent);
    if (!asset.mergedGeometry) return;

    const oldMesh = pool.instancedMesh;
    const newMesh = new THREE.InstancedMesh(asset.mergedGeometry, FurnitureAssets.instancedMaterials, newCapacity);

    newMesh.frustumCulled = false;
    newMesh.renderOrder = ENTITY_RENDER_ORDER.furniture;

    // 重建实例映射和矩阵
    const newEntityToInstanceId = new Map<string, number>();
    let newNextInstanceId = 0;

    for (const [entityId, oldInstanceId] of pool.entityToInstanceId) {
      const newInstanceId = newNextInstanceId++;
      newEntityToInstanceId.set(entityId, newInstanceId);

      // 复制矩阵数据
      const matrix = new THREE.Matrix4();
      oldMesh.getMatrixAt(oldInstanceId, matrix);
      newMesh.setMatrixAt(newInstanceId, matrix);

      // 更新注册信息中的 instanceId
      const registration = this._entityRegistrations.get(entityId);
      if (registration) {
        registration.instanceId = newInstanceId;
      }
    }

    // 初始化剩余空间为隐藏
    const hideMatrix = new THREE.Matrix4().makeScale(0, 0, 0);
    for (let i = newNextInstanceId; i < newCapacity; i++) {
      newMesh.setMatrixAt(i, hideMatrix);
    }

    newMesh.instanceMatrix.needsUpdate = true;

    // 替换场景中的 mesh
    this.scene.remove(oldMesh);
    newMesh.position.setZ(this._displayOrder);
    this.scene.add(newMesh);

    oldMesh.dispose();

    // 更新 pool
    pool.instancedMesh = newMesh;
    pool.entityToInstanceId = newEntityToInstanceId;
    pool.nextInstanceId = newNextInstanceId;
    pool.freeIds = [];
  }

  /**
   * 销毁指定的 pool，释放内存和减少 drawCall
   */
  private disposePool(modelId: string): void {
    const pool = this._pools.get(modelId);
    if (!pool) return;

    // 从场景中移除并销毁 InstancedMesh
    this.scene.remove(pool.instancedMesh);
    pool.instancedMesh.dispose();

    // 从 dirty pools 中移除
    this._dirtyPools.delete(pool);

    // 从 pools map 中删除
    this._pools.delete(modelId);
  }

  /**
   * 清理所有资源
   */
  dispose(): void {
    this._pools.forEach((pool) => {
      this.scene.remove(pool.instancedMesh);
      pool.instancedMesh.dispose();
    });
    this._pools.clear();
    this._entityRegistrations.clear();
    this._entityZLevel.clear();
    this._zLevelCounter = 0;
    this._dirtyPools.clear();
  }
}
